if (!customElements.get('external-stock')) {
  class ExternalStock extends HTMLElement {
    constructor() {
      super();

      this.lang = this.dataset.lang || document.documentElement.lang || 'fr';
      this.texts = this.getTranslations(this.lang);

      this.loadingEl = this.querySelector('.external-stock__loading');
      this.shopifyEl = this.querySelector('.external-stock__shopify');
      this.shopifyMessageEl = this.querySelector('.external-stock__shopify-message');
      this.supplierEl = this.querySelector('.external-stock__supplier');
      this.supplierMessageEl = this.querySelector('.external-stock__supplier-message');
      this.transitEl = this.querySelector('.external-stock__transit');
      this.transitMessageEl = this.querySelector('.external-stock__transit-message');

      this.shopifyInStock = this.dataset.shopifyInStock === 'true';
      this.shopifyQty = parseInt(this.dataset.shopifyQty || '0', 10);
      this.showQuantities = this.dataset.showQuantities === 'true';
      this.hasSupplierLine = !!this.supplierEl;
      this.hasTransitLine = !!this.transitEl;

      this.linesRendered = 0;
    }

    getTranslations(lang) {
      const translations = {
        fr: {
          available: 'Disponible',
          singular: 'disponible',
          plural: 'disponibles',
          order: 'Disponible sur commande',
          transit: 'en transit',
          on_date: 'le',
          under: 'sous'
        },
        en: {
          available: 'Available',
          singular: 'available',
          plural: 'available',
          order: 'Available on order',
          transit: 'in transit',
          on_date: 'on',
          under: 'under'
        }
      };
      return translations[lang] || translations['fr'];
    }

    connectedCallback() {
      const sku = this.dataset.sku;

      // Affiche le stock Shopify immédiatement (pas besoin d'attendre l'API)
      if (this.shopifyInStock && this.shopifyQty > 0) {
        this.renderShopify(this.shopifyQty);
      } else {
        this.applyFallback();
      }

      if (!sku) return;

      this.fetchStock(sku);
    }

    async fetchStock(sku) {
      const API_URL = '/apps/shapi/stocks/search';

      try {
        const response = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [sku] }),
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data = await response.json();
        this.renderResult(data);
      } catch (error) {
        console.error('[ExternalStock] Fetch error:', error);
        // Ne rien faire : Shopify ou fallback est déjà affiché
      }
    }

    renderResult(data) {
      const product = Array.isArray(data) ? data[0] : null;

      if (!product) return; // Shopify ou fallback déjà affiché

      // Fournisseur
      const remoteStock = product.total_qty || 0;
      const leadText = product[this.lang] || null;
      if (this.hasSupplierLine && remoteStock > 0 && leadText) {
        this.renderSupplier(remoteStock, leadText);
      }

      // Transit
      const inTransit = (product.warehouses || []).reduce(
        (sum, w) => sum + Number(w.in_transit_qty || 0),
        0,
      );
      const leadEndDate = product.lead_end_time || null;
      if (this.hasTransitLine && inTransit > 0) {
        const dateStr = leadEndDate ? this.formatDate(leadEndDate) : null;
        const suffix = dateStr ? `${this.texts.on_date} ${dateStr}` : this.texts.transit;
        this.renderTransit(inTransit, suffix);
      }

      // Si on a affiché des lignes supplémentaires (fournisseur ou transit),
      // masque le fallback "Disponible sur commande"
      if (this.linesRendered > 0) {
        const mainEl = this.querySelector('.external-stock__main');
        if (mainEl) mainEl.hidden = true;
        this.hideLoading();
      }

      // Si on a plusieurs lignes, ajoute la classe pour retirer les badges secondaires
      this.updateMultipleLinesClass();
    }

    updateMultipleLinesClass() {
      if (this.linesRendered > 1) {
        this.classList.add('has-multiple-lines');
      } else {
        this.classList.remove('has-multiple-lines');
      }
    }

    renderShopify(qty) {
      if (!this.shopifyMessageEl || !this.shopifyEl) return;
      this.shopifyMessageEl.innerHTML = this.formatWithQtyHtml(qty);
      this.shopifyEl.hidden = false;
      this.setLinePositionClass(this.shopifyEl);
      this.linesRendered++;
      const mainEl = this.querySelector('.external-stock__main');
      if (mainEl) mainEl.hidden = true;
    }

    renderSupplier(qty, leadText) {
      if (!this.supplierMessageEl || !this.supplierEl) return;
      const suffixEl = this.supplierEl.querySelector('.external-stock__supplier-suffix');
      
      if (this.linesRendered === 0) {
        // Première ligne : quantité dans le badge, suffixe hors badge
        const qtyText = this.formatWithQty(qty);
        this.supplierMessageEl.innerHTML = `<span class="stock-qty">${qtyText}</span>`;
        if (suffixEl) suffixEl.textContent = `${this.texts.under} ${leadText}`;
      } else {
        // Ligne secondaire : tout dans le badge
        const html = this.formatWithQtyHtml(qty, `${this.texts.under} ${leadText}`);
        this.supplierMessageEl.innerHTML = html;
        if (suffixEl) suffixEl.textContent = '';
      }
      
      this.supplierEl.hidden = false;
      this.setLinePositionClass(this.supplierEl);
      this.linesRendered++;
    }

    renderTransit(qty, suffix) {
      if (!this.transitMessageEl || !this.transitEl) return;
      const suffixEl = this.transitEl.querySelector('.external-stock__transit-suffix');
      
      if (this.linesRendered === 0) {
        // Première ligne : quantité dans le badge, suffixe hors badge
        const qtyText = this.formatWithQty(qty);
        this.transitMessageEl.innerHTML = `<span class="stock-qty">${qtyText}</span>`;
        if (suffixEl) suffixEl.textContent = suffix;
      } else {
        // Ligne secondaire : tout dans le badge
        const html = this.formatWithQtyHtml(qty, suffix);
        this.transitMessageEl.innerHTML = html;
        if (suffixEl) suffixEl.textContent = '';
      }
      
      this.transitEl.hidden = false;
      this.setLinePositionClass(this.transitEl);
      this.linesRendered++;
    }

    setLinePositionClass(el) {
      if (this.linesRendered === 0) {
        el.classList.add('first-line');
        el.classList.remove('secondary-line');
      } else {
        el.classList.add('secondary-line');
        el.classList.remove('first-line');
      }
    }

    formatWithQty(qty, suffix) {
      if (!this.showQuantities || !qty || qty <= 0) {
        return suffix ? `${this.texts.available} ${suffix}` : this.texts.available;
      }

      const word = qty > 1 ? this.texts.plural : this.texts.singular;
      return suffix ? `${qty} ${word} ${suffix}` : `${qty} ${word}`;
    }

    formatWithQtyHtml(qty, suffix) {
      if (!this.showQuantities || !qty || qty <= 0) {
        return suffix ? `${this.texts.available} ${suffix}` : this.texts.available;
      }

      const word = qty > 1 ? this.texts.plural : this.texts.singular;
      const qtyText = `${qty} ${word}`;
      return suffix
        ? `<span class="stock-qty">${qtyText}</span> <span class="stock-lead">${suffix}</span>`
        : `<span class="stock-qty">${qtyText}</span>`;
    }

    applyFallback() {
      const mainEl = this.querySelector('.external-stock__main');
      const mainMsgEl = this.querySelector('.external-stock__main-message');
      if (mainEl) {
        mainEl.hidden = false;
      }
      if (mainMsgEl) {
        mainMsgEl.textContent = this.texts.order;
      }
      this.hideLoading();
    }

    hideLoading() {
      if (this.loadingEl) this.loadingEl.hidden = true;
    }

    formatDate(dateString) {
      if (!dateString) return '';

      const datePart = dateString.split('T')[0].split(' ')[0];
      const [year, month, day] = datePart.split('-');

      if (!year || !month || !day) return '';

      return `${day}/${month}/${year}`;
    }
  }

  customElements.define('external-stock', ExternalStock);
}
